import { AxiosError } from 'axios'
import { ROUTES } from '@/constants/routes'
import type { DDSMood, DDSOutput, UserProfile } from '@/types'

const CHECK_IN_TOKEN_PARAM = 't'

// Link do QR: abre pela câmera nativa ou pelo scanner do app
export function buildCheckInUrl(token: string): string {
	const url = new URL(ROUTES.DDS.CHECK_IN, window.location.origin)
	url.searchParams.set(CHECK_IN_TOKEN_PARAM, token)
	return url.toString()
}

export function getCheckInTokenParam(params: URLSearchParams): string | null {
	return params.get(CHECK_IN_TOKEN_PARAM)
}

// Aceita o link completo do QR ou o token puro; devolve null para QR de outro tipo
export function extractCheckInToken(raw: string): string | null {
	const value = raw.trim()
	try {
		const url = new URL(value)
		if (!url.pathname.endsWith(ROUTES.DDS.CHECK_IN)) return null
		return url.searchParams.get(CHECK_IN_TOKEN_PARAM)
	} catch {
		return /^[\w-]+\.[\w-]+\.[\w-]+$/.test(value) ? value : null
	}
}

const ERROR_KEYS: Record<string, string> = {
	ATTENDANCE_TOKEN_EXPIRED: 'ddsCheckIn.errors.expired',
	ATTENDANCE_TOKEN_INVALID: 'ddsCheckIn.errors.invalid',
	DDS_CLOSED: 'ddsCheckIn.errors.closed',
	NOT_FOUND: 'ddsCheckIn.errors.notFound',
	FORBIDDEN: 'ddsAttendance.errors.forbidden',
}

export function getAttendanceErrorCode(error: unknown): string | undefined {
	return error instanceof AxiosError
		? error.response?.data?.error_code
		: undefined
}

export function getAttendanceErrorKey(error: unknown): string | undefined {
	const code = getAttendanceErrorCode(error)
	return code ? ERROR_KEYS[code] : undefined
}

export function canManageDdsAttendance(
	user: UserProfile | null,
	dds: Pick<DDSOutput, 'createdById'> | undefined,
): boolean {
	if (!user || !dds) return false
	return user.role === 'ADMIN' || user.personId === dds.createdById
}

//TODO: revisar se os papéis listados em EMOTION_REVIEW_ROLES estão corretos e se precisam ser atualizados conforme a política de acesso do backend.
const EMOTION_REVIEW_ROLES: UserProfile['role'][] = [
	'ADMIN',
	'SSMA_MANAGER',
	'MANAGER',
	'TECHNICIAN',
]

// Mesma regra do backend: emociograma é dado sensível, só quem criou o DDS e quem acompanha a equipe vê
export function canReviewDdsEmotions(
	user: UserProfile | null,
	dds: Pick<DDSOutput, 'createdById'> | undefined,
): boolean {
	if (!user || !dds) return false
	return (
		EMOTION_REVIEW_ROLES.includes(user.role) ||
		user.personId === dds.createdById
	)
}

// Só quem está triste pode ter ação registrada (o backend aceita qualquer humor)
export function canRegisterDdsMoodAction(mood: DDSMood | null): boolean {
	return mood === 'SAD'
}

// closesAt vem do backend (prazo definido na criação do DDS)
export function isDdsOpen(closesAt: string | undefined, now = Date.now()) {
	return !!closesAt && new Date(closesAt).getTime() > now
}

export function formatDurationMinutes(minutes: number): string {
	return minutes >= 60 && minutes % 60 === 0
		? `${minutes / 60} h`
		: `${minutes} min`
}
